import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Percent, Clock, CheckCircle2, AlertTriangle, TrendingUp } from "lucide-react";

export default function PortalFrequenza() {
  const { data: me } = useQuery({
    queryKey: ["auth-me"],
    queryFn: async () => (await api.get("/auth/me")).data,
  });
  const { data: coursePct = [], isLoading } = useQuery({
    enabled: !!me?.student_id,
    queryKey: ["my-attendance-percentages", me?.student_id],
    queryFn: async () => (await api.get(`/attendance/students/${me.student_id}/course-percentages`)).data,
  });

  const totals = useMemo(() => {
    const t = { attended: 0, expected: 0, late: 0, absent: 0 };
    coursePct.forEach((c) => {
      t.attended += c.attended_hours || 0;
      t.expected += c.total_course_hours || 0;
      t.late += c.late_hours || 0;
      t.absent += c.absent_hours || 0;
    });
    return {
      ...t,
      pct: t.expected ? Math.round((t.attended / t.expected) * 100 * 10) / 10 : 0,
    };
  }, [coursePct]);

  const colorFor = (pct) => (pct >= 75 ? "#16a34a" : pct >= 50 ? "#f59e0b" : "#dc2626");
  const statusFor = (pct) => (pct >= 75 ? "OK" : pct >= 50 ? "Attenzione" : "Critico");
  const statusClass = (pct) =>
    pct >= 75
      ? "bg-emerald-50 text-emerald-800 border-emerald-200"
      : pct >= 50
        ? "bg-amber-50 text-amber-800 border-amber-200"
        : "bg-red-50 text-red-800 border-red-200";

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1200px] mx-auto" data-testid="portal-frequenza">
      <PageHeader
        title="La mia frequenza"
        subtitle="Ore frequentate per ciascun corso rispetto al monte ore totale. La % viene usata anche per l'accesso agli appelli."
      />

      {/* KPI riepilogo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Kpi label="Frequenza media" value={`${totals.pct}%`} icon={Percent} accent={colorFor(totals.pct)} />
        <Kpi label="Ore frequentate" value={`${totals.attended}h`} icon={CheckCircle2} accent="#1C352D" />
        <Kpi label="Monte ore atteso" value={`${totals.expected}h`} icon={Clock} accent="#1C352D" />
        <Kpi label="Ore di assenza" value={`${totals.absent}h`} icon={AlertTriangle} accent="#dc2626" />
      </div>

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 border-b border-stone-100 text-sm font-medium flex items-center justify-between">
          <span className="flex items-center gap-2"><TrendingUp size={14}/> Dettaglio per corso</span>
          <span className="text-[11px] text-stone-500 font-normal">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1 align-middle"/> ≥75%
            <span className="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1 ml-2 align-middle"/> 50-74%
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1 ml-2 align-middle"/> &lt;50%
          </span>
        </div>
        {isLoading ? (
          <div className="p-8 text-sm text-stone-400 text-center">Caricamento…</div>
        ) : coursePct.length === 0 ? (
          <div className="p-10 text-sm text-stone-500 text-center">
            Nessuna frequenza ancora registrata. Le presenze inizieranno a comparire qui appena i tuoi docenti apriranno le prime lezioni.
          </div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {coursePct.map((c, i) => {
              const pct = c.attendance_percentage || 0;
              const color = colorFor(pct);
              return (
                <li key={c.course_id} className="p-4 md:p-5 animate-in-row" style={{ animationDelay: `${i * 20}ms` }} data-testid={`frequenza-course-${c.course_id}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="font-medium text-[#1C352D] truncate flex items-center gap-2 flex-wrap">
                        {c.course_name}
                        {c.is_seminar && <Badge className="bg-[#E2725B]/10 text-[#8f3e26] border border-[#E2725B]/30 text-[10px] rounded-full">SEMINARIO</Badge>}
                        {c.course_code && <span className="text-[10px] mono text-stone-400">{c.course_code}</span>}
                      </div>
                      <div className="text-[11px] text-stone-500 mt-0.5 flex flex-wrap gap-x-2">
                        <span><b className="text-[#1C352D]">{c.attended_hours}h</b> frequentate su <b className="text-[#1C352D]">{c.total_course_hours}h</b> totali</span>
                        {c.late_hours > 0 && <span>· {c.late_hours}h ritardo</span>}
                        {c.absent_hours > 0 && <span className="text-red-700">· {c.absent_hours}h assenza</span>}
                        {c.justified_hours > 0 && <span className="text-stone-500">· {c.justified_hours}h giustificate</span>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-2xl font-semibold" style={{ color }}>{pct}%</div>
                      <Badge className={`text-[10px] rounded-full border mt-0.5 ${statusClass(pct)}`}>{statusFor(pct)}</Badge>
                    </div>
                  </div>
                  <div className="w-full h-2.5 bg-stone-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all" style={{ width: `${Math.min(100, pct)}%`, background: color }}/>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <div className="text-[11px] text-stone-500 mt-4 text-center">
        Il ritardo viene conteggiato come presenza. Le ore giustificate non incidono sulla % ma sono tracciate per completezza.
      </div>
    </div>
  );
}

function Kpi({ label, value, icon: Icon, accent }) {
  return (
    <Card className="p-4 border border-stone-200 rounded-xl shadow-none">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5">
        <Icon size={11} style={{ color: accent }}/> {label}
      </div>
      <div className="text-2xl font-semibold mt-1" style={{ color: accent }}>{value}</div>
    </Card>
  );
}
